import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Star,
  Search,
  Heart,
  Trash2,
  Building,
  ChevronRight,
  ChevronDown,
  Lightbulb,
  Sparkles
} from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { supabase } from '../../../lib/supabase';
import EssayTopicDevelopment from './EssayTopicDevelopment';
import EssayIdeasLibrary from './EssayIdeasLibrary';
import ChoiceGroupCard from '../Essays/ChoiceGroupCard';
import EssaySlotService from '../../../services/EssaySlotService';
import WhySchoolBuilder from '../WhySchool/WhySchoolBuilder';

/**
 * Essay Slots Library
 * 
 * Displays all essay requirements (slots) grouped by college
 * Shows supplemental essays with their prompts and word limits
 */
export default function EssaySlotsLibrary({ onNavigateToOutlines, refreshKey, expandedCollegeId = null }) {
  const { currentUser } = useFamilyContext();
  const [slots, setSlots] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('college'); // 'college' or 'ideas'
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [expandedColleges, setExpandedColleges] = useState(
    expandedCollegeId ? { [expandedCollegeId]: true } : {}
  );
  const [developingSlot, setDevelopingSlot] = useState(null);
  const [whySchoolBuilderSlot, setWhySchoolBuilderSlot] = useState(null);

  // Fetch both essay slots and ideas
  const fetchData = useCallback(async () => {
    if (!currentUser?.id) return;
    
    try {
      setLoading(true);
      setError(null);

      // Fetch essay slots (college requirements)
      const { data: slotsData, error: slotsError } = await supabase
        .from('essay_slots')
        .select(`
          *,
          college:college_journey_colleges(
            id,
            name,
            category,
            global_colleges(
              name,
              location
            )
          )
        `)
        .eq('user_id', currentUser.id)
        .not('prompt_text', 'is', null)
        .order('created_at', { ascending: false });

      if (slotsError) throw slotsError;

      // Fetch essay ideas (brainstormed topics - excluding those linked to slots)
      const { data: ideasData, error: ideasError } = await supabase
        .from('college_journey_essay_ideas')
        .select('*')
        .eq('user_id', currentUser.id)
        .is('essay_slot_id', null) // Only show standalone ideas, not slot-linked ones
        .order('created_at', { ascending: false });

      if (ideasError) throw ideasError;

      console.log(`✅ Loaded ${slotsData.length} essay slots and ${ideasData.length} ideas`);
      setSlots(slotsData || []);
      setIdeas(ideasData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const toggleFavorite = async (itemId, currentlyFavorited, isIdea = false) => {
    try {
      const table = isIdea ? 'college_journey_essay_ideas' : 'essay_slots';
      const { error: updateError} = await supabase
        .from(table)
        .update({ is_favorite: !currentlyFavorited })
        .eq('id', itemId);

      if (updateError) throw updateError;

      if (isIdea) {
        setIdeas(ideas.map(idea =>
          idea.id === itemId ? { ...idea, is_favorite: !currentlyFavorited } : idea
        ));
      } else {
        setSlots(slots.map(slot =>
          slot.id === itemId ? { ...slot, is_favorite: !currentlyFavorited } : slot
        ));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const toggleCollege = (collegeId) => {
    setExpandedColleges(prev => ({
      ...prev,
      [collegeId]: !prev[collegeId]
    }));
  };

  // Handle updating slot selection status (exploratory, finalized, rejected)
  const handleUpdateSlotStatus = async (slotId, newStatus) => {
    try {
      await EssaySlotService.updateSlotSelectionStatus(slotId, newStatus);

      // Update local state
      setSlots(slots.map(slot =>
        slot.id === slotId ? { ...slot, selection_status: newStatus } : slot
      ));
    } catch (error) {
      console.error('Error updating slot status:', error);
    }
  };

  // Handle starting essay development for a supplemental slot
  const handleStartWriting = async (slot) => {
    console.log('Starting development for slot:', slot);

    // First, check if there's already an essay idea for this slot
    const { data: existingIdea } = await supabase
      .from('college_journey_essay_ideas')
      .select('*')
      .eq('essay_slot_id', slot.id)
      .maybeSingle();

    if (existingIdea) {
      // Use existing idea
      console.log('Found existing idea for slot:', existingIdea);
      setDevelopingSlot(existingIdea);
    } else {
      // Create a new essay idea from the slot
      const newIdea = {
        id: null, // Temporary ID - will be created on save
        title: `${slot.college?.global_colleges?.name || slot.college?.name} - ${slot.slot_type}`,
        essay_type: 'supplemental',
        prompt: slot.prompt_text,
        word_limit: slot.word_limit,
        school_name: slot.college?.global_colleges?.name || slot.college?.name,
        college_id: slot.college_id,
        essay_slot_id: slot.id,
        supplemental_context: slot.supplemental_context || {}
      };

      console.log('Created temporary idea for slot:', newIdea);
      setDevelopingSlot(newIdea);
    }
  };

  // Filter slots (college essays)
  const filteredSlots = slots.filter(slot => {
    // Filter out rejected slots unless showRejected is true
    if (!showRejected && slot.selection_status === 'rejected') return false;

    if (filter !== 'all' && slot.slot_type !== filter) return false;
    if (favoritesOnly && !slot.is_favorite) return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const collegeName = slot.college?.global_colleges?.name || slot.college?.name || '';
      return (
        slot.prompt_text?.toLowerCase().includes(query) ||
        collegeName.toLowerCase().includes(query) ||
        slot.slot_type?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Filter ideas (my brainstormed topics)
  const filteredIdeas = ideas.filter(idea => {
    if (filter !== 'all' && idea.source_type !== filter) return false;
    if (favoritesOnly && !idea.is_favorite) return false;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        idea.title?.toLowerCase().includes(query) ||
        idea.description?.toLowerCase().includes(query) ||
        idea.essay_angle?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Group slots by college and separate choice groups
  const groupedSlots = filteredSlots.reduce((acc, slot) => {
    const collegeId = slot.college_id || 'unknown';
    const collegeName = slot.college?.global_colleges?.name || slot.college?.name || 'Personal Essay';

    if (!acc[collegeId]) {
      acc[collegeId] = {
        name: collegeName,
        category: slot.college?.category || '',
        location: slot.college?.global_colleges?.location || '',
        slots: [],
        choiceGroups: {}
      };
    }

    // If slot is part of a choice group, group it
    if (slot.prompt_group_id) {
      if (!acc[collegeId].choiceGroups[slot.prompt_group_id]) {
        acc[collegeId].choiceGroups[slot.prompt_group_id] = [];
      }
      acc[collegeId].choiceGroups[slot.prompt_group_id].push(slot);
    } else {
      // Regular independent slot
      acc[collegeId].slots.push(slot);
    }

    return acc;
  }, {});

  // Count rejected slots for notice
  const rejectedCount = slots.filter(s => s.selection_status === 'rejected').length;

  // Group ideas by source type
  const groupedIdeas = filteredIdeas.reduce((acc, idea) => {
    const source = idea.source_type || 'manual';
    if (!acc[source]) acc[source] = [];
    acc[source].push(idea);
    return acc;
  }, {});

  // Category colors
  const getCategoryColor = (category) => {
    switch(category) {
      case 'reach': return { dot: 'bg-coral-500', border: 'border-coral-800', bg: 'bg-coral-50', text: 'text-coral-900' };
      case 'target': return { dot: 'bg-teal-500', border: 'border-teal-800', bg: 'bg-teal-50', text: 'text-teal-900' };
      case 'safety': return { dot: 'bg-emerald-500', border: 'border-emerald-800', bg: 'bg-emerald-50', text: 'text-emerald-900' };
      default: return { dot: 'bg-slate-400', border: 'border-slate-800', bg: 'bg-slate-50', text: 'text-slate-900' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-6 h-6 text-teal-600 animate-pulse" />
          </div>
          <p className="text-slate-600">Loading essay requirements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-coral-50 border border-coral-200 rounded-lg p-6 text-center">
        <p className="text-coral-700 mb-4">Failed to load data: {error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-coral-600 text-white rounded-lg hover:bg-coral-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {/* Header */}
      <div className="bg-slate-900 rounded-lg p-6 border-b-2 border-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-white mb-2">Topics & Ideas</h1>
              <p className="text-sm text-stone-300">
                {viewMode === 'college' ? (
                  <>
                    {filteredSlots.length} {filteredSlots.length === 1 ? 'essay' : 'essays'} across {Object.keys(groupedSlots).length} {Object.keys(groupedSlots).length === 1 ? 'college' : 'colleges'}
                  </>
                ) : (
                  <>
                    {filteredIdeas.length} {filteredIdeas.length === 1 ? 'idea' : 'ideas'} from brainstorming sessions
                  </>
                )}
                {favoritesOnly && ' (favorites only)'}
              </p>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex gap-2 border-2 border-white rounded-lg p-1">
              <button
                onClick={() => setViewMode('college')}
                className={`px-4 py-2 rounded text-xs font-medium transition-all ${
                  viewMode === 'college'
                    ? 'bg-white text-slate-900'
                    : 'text-white hover:bg-slate-800'
                }`}
              >
                <Building className="w-3.5 h-3.5 inline mr-1.5" />
                College Essays
              </button>
              <button
                onClick={() => setViewMode('ideas')}
                className={`px-4 py-2 rounded text-xs font-medium transition-all ${
                  viewMode === 'ideas'
                    ? 'bg-white text-slate-900'
                    : 'text-white hover:bg-slate-800'
                }`}
              >
                <Lightbulb className="w-3.5 h-3.5 inline mr-1.5" />
                My Ideas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search - Only show for College Essays view */}
      {viewMode === 'college' && (
        <div className="border-b-2 border-slate-900">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <input
                  type="text"
                  placeholder="Search college essays..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-stone-500 focus:border-teal-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Filter */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="appearance-none px-4 py-3 pr-10 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 font-medium focus:border-teal-500 focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="all">All Essay Types</option>
                  <option value="supplemental">Supplemental</option>
                  <option value="personal_statement">Personal Statement</option>
                  <option value="short_answer">Short Answer</option>
                  <option value="why_us">Why Us</option>
                  <option value="activity">Activity</option>
                </select>
              </div>

              {/* Favorites Button */}
              <button
                onClick={() => setFavoritesOnly(!favoritesOnly)}
                className={`px-4 py-3 bg-white border rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                  favoritesOnly
                    ? 'border-coral-500 text-coral-600 bg-coral-50'
                    : 'border-slate-300 text-slate-900 hover:border-slate-900 hover:shadow-sm'
                }`}
              >
                <Heart className={`w-4 h-4 ${favoritesOnly ? 'fill-coral-600' : ''}`} />
                Favorites
              </button>

              {/* Show Rejected Toggle */}
              {rejectedCount > 0 && (
                <label className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm cursor-pointer hover:border-slate-900 hover:shadow-sm transition-all">
                  <input
                    type="checkbox"
                    checked={showRejected}
                    onChange={(e) => setShowRejected(e.target.checked)}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <span className="text-slate-900 font-medium">
                    Show rejected ({rejectedCount})
                  </span>
                </label>
              )}
            </div>

            {/* Rejected notice */}
            {rejectedCount > 0 && !showRejected && (
              <div className="mt-4 p-3 bg-slate-100 border border-slate-200 rounded-lg flex items-center gap-2 text-sm">
                <span className="text-slate-600">
                  {rejectedCount} prompt{rejectedCount > 1 ? 's' : ''} marked as not pursuing.
                </span>
                <button
                  onClick={() => setShowRejected(true)}
                  className="text-teal-600 font-medium hover:text-teal-700"
                >
                  Show
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* College Essays View */}
        {viewMode === 'college' && (
          <>
            {/* Empty State */}
            {filteredSlots.length === 0 && (
              <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {searchQuery || favoritesOnly ? 'No matching essays' : 'No essay requirements yet'}
                </h3>
                <p className="text-slate-600 mb-4">
                  {searchQuery || favoritesOnly
                    ? 'Try adjusting your filters or search query'
                    : 'Add colleges to see their essay requirements'}
                </p>
              </div>
            )}

            {/* College Panels - Collapsible */}
            <div className="space-y-4">
              {Object.entries(groupedSlots).map(([collegeId, college]) => {
                const colors = getCategoryColor(college.category);
                const isExpanded = expandedColleges[collegeId] === true; // Default to collapsed

                return (
                  <div key={collegeId} className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
                    {/* College Header - Clickable to expand/collapse */}
                    <button
                      onClick={() => toggleCollege(collegeId)}
                      className="w-full px-4 md:px-6 py-4 bg-stone-50 border-b border-stone-200 hover:bg-stone-100 transition-colors text-left"
                    >
                      {/* Row 1: College Name */}
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-slate-900 font-bold text-base md:text-lg">{college.name}</h2>
                        {college.category && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
                            {college.category}
                          </span>
                        )}
                      </div>

                      {/* Row 2: Icons and metadata */}
                      <div className="flex items-center gap-3">
                        <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                        <Building className="w-4 h-4 text-slate-600" />
                        <div className={`w-2 h-2 rounded-full ${colors.dot}`}></div>
                        <span className="text-stone-600 text-xs">
                          {college.slots.length} {college.slots.length === 1 ? 'essay' : 'essays'}
                        </span>
                      </div>
                    </button>

                    {/* Essay Slots Grid - Collapsible */}
                    {isExpanded && (
                      <div className="p-6 space-y-6">
                        {/* Render all slots (choice groups and independent) in the same grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Render choice groups */}
                          {Object.entries(college.choiceGroups).map(([groupId, groupSlots]) => (
                            <ChoiceGroupCard
                              key={groupId}
                              slots={groupSlots}
                              onUpdateStatus={handleUpdateSlotStatus}
                              onDevelopIdea={handleStartWriting}
                            />
                          ))}

                          {/* Render independent slots */}
                  {college.slots.map((slot) => (
                    <div 
                      key={slot.id}
                      className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden hover:shadow-xl transition-all"
                    >
                      {/* Card Header */}
                      <div className="px-6 py-4 bg-stone-50 border-b border-stone-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                              {slot.slot_type?.replace(/_/g, ' ') || 'Supplemental Essay'}
                            </h3>
                          </div>
                          <button
                            onClick={() => toggleFavorite(slot.id, slot.is_favorite)}
                            className="text-stone-400 hover:text-coral-500 transition-colors"
                          >
                            <Heart className={`w-4 h-4 ${slot.is_favorite ? 'fill-coral-500 text-coral-500' : ''}`} />
                          </button>
                        </div>
                      </div>

                      {/* Prompt Text */}
                      <div className="px-6 py-6">
                        <div className="border border-slate-900 rounded-lg p-4 mb-4">
                          <p className="text-sm text-slate-900 leading-relaxed">{slot.prompt_text}</p>
                        </div>

                        {/* Word Limit */}
                        {slot.word_limit && (
                          <p className="text-xs text-stone-600 mb-4">
                            <span className="font-semibold">Word Limit:</span> {slot.word_limit} words
                          </p>
                        )}

                        {/* Optional Tag */}
                        {slot.is_optional && (
                          <div className="inline-block px-2 py-1 bg-stone-100 border border-stone-300 text-slate-700 rounded text-xs font-medium mb-4">
                            Optional
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="px-6 py-4 bg-stone-50 border-t border-stone-200">
                        {(() => {
                          // Detect if this is a "Why School" or "Why Major" essay
                          const promptLower = slot.prompt_text?.toLowerCase() || '';
                          const isWhySchool = promptLower.includes('why') && (
                            promptLower.includes('university') ||
                            promptLower.includes('college') ||
                            promptLower.includes(college.name.toLowerCase())
                          );
                          const isWhyMajor = promptLower.includes('why') && (
                            promptLower.includes('major') ||
                            promptLower.includes('field of study') ||
                            promptLower.includes('area of study')
                          );

                          if (isWhySchool || isWhyMajor) {
                            // Show both Develop Idea and Why School Builder buttons
                            return (
                              <div className="space-y-2">
                                <button
                                  onClick={() => setWhySchoolBuilderSlot(slot)}
                                  className="w-full px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium flex items-center justify-center gap-2"
                                >
                                  <Building className="w-3.5 h-3.5" />
                                  Why School/Major Builder
                                </button>
                                <button
                                  onClick={() => handleStartWriting(slot)}
                                  className="w-full px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors text-xs font-medium flex items-center justify-center gap-2"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  Develop Idea
                                </button>
                              </div>
                            );
                          }

                          // Default: Just Develop Idea button
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleStartWriting(slot)}
                                className="flex-1 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium flex items-center justify-center gap-2"
                              >
                                <FileText className="w-3.5 h-3.5" />
                                Develop Idea
                              </button>
                              <button
                                className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors text-xs font-medium"
                              >
                                <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* My Ideas View - Render full EssayIdeasLibrary component */}
        {viewMode === 'ideas' && (
          <EssayIdeasLibrary
            onNavigateToOutlines={onNavigateToOutlines}
            refreshKey={refreshKey}
            hideHeader={true}
          />
        )}
      </div>

      {/* Essay Topic Development Modal */}
      {developingSlot && (
        <EssayTopicDevelopment
          essayIdea={developingSlot}
          onComplete={(result) => {
            console.log('Development complete:', result);
            setDevelopingSlot(null);
            // Refresh data to get updated ideas
            fetchData();
          }}
          onClose={() => setDevelopingSlot(null)}
          onNavigateToOutlines={onNavigateToOutlines}
        />
      )}

      {/* Why School Builder Modal */}
      {whySchoolBuilderSlot && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className="h-full overflow-auto">
            <WhySchoolBuilder
              essayId={whySchoolBuilderSlot.id}
              collegeId={whySchoolBuilderSlot.college_id}
              userId={currentUser?.id}
              essaySubtype={whySchoolBuilderSlot.prompt_text?.toLowerCase().includes('major') ? 'why_major' : 'why_school'}
              onClose={() => setWhySchoolBuilderSlot(null)}
              onSave={() => {
                setWhySchoolBuilderSlot(null);
                fetchData(); // Refresh data
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

